import { useMemo, useState } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { useOrders } from '../../hooks/useOrders';
import { useCurrency } from '../../hooks/useCurrency';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FileSpreadsheet, FileText, Download, RefreshCw, BarChart3, UtensilsCrossed, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Order } from '../../services/orders.service';

interface ReportsPageProps {
  onNavigate?: (page: string) => void;
}

type DateRangeType = 'all' | 'today' | 'week' | 'month' | 'year' | 'custom';
type ReportType = 'orders' | 'topItems' | 'tableUsage';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PREPARING', label: 'Preparing' },
  { value: 'READY', label: 'Ready' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'REFUNDED', label: 'Refunded' },
];

function getDateRange(range: DateRangeType, customStart?: string, customEnd?: string) {
  const now = new Date();
  const end = new Date(now);
  let start: Date | null = null;

  switch (range) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week': {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      start = new Date(now);
      start.setDate(now.getDate() - diff);
      start.setHours(0, 0, 0, 0);
      break;
    }
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    case 'custom':
      if (customStart && customEnd) {
        start = new Date(customStart);
        end.setTime(new Date(customEnd).getTime());
        end.setHours(23, 59, 59, 999);
      }
      break;
    default:
      break;
  }

  return { start, end };
}

function getOrderTableName(order: Order): string | null {
  const raw = order as any;
  if (raw.table?.name) {
    return String(raw.table.name);
  }

  const notes = String(order.notes || '').trim();
  const match = notes.match(/table\s*:\s*([^\n]+)/i);
  return match?.[1]?.trim() || null;
}

export function ReportsPage({ onNavigate }: ReportsPageProps) {
  usePageTitle('Reports');

  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 768 : true));
  const [reportType, setReportType] = useState<ReportType>('orders');
  const [selectedStatus, setSelectedStatus] = useState('CLOSED');
  const [dateRange, setDateRange] = useState<DateRangeType>('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const { orders: ordersData, loading, error, refresh, stats } = useOrders({ page: 1, limit: 500 });
  const { getCurrencySymbol } = useCurrency();

  const formatReportMoney = (amount: number | string) => {
    const numericAmount = Number(amount || 0);
    const safeAmount = isNaN(numericAmount) ? 0 : numericAmount;
    return `${getCurrencySymbol()} ${safeAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatReportMoneyCompact = (amount: number | string) => {
    const numericAmount = Number(amount || 0);
    const safeAmount = isNaN(numericAmount) ? 0 : numericAmount;

    let displayValue: string;
    if (Math.abs(safeAmount) >= 1000000000) {
      displayValue = (safeAmount / 1000000000).toFixed(1) + 'B';
    } else if (Math.abs(safeAmount) >= 1000000) {
      displayValue = (safeAmount / 1000000).toFixed(1) + 'M';
    // } else if (Math.abs(safeAmount) >= 1000) {
    //   displayValue = (safeAmount / 1000).toFixed(1) + 'K';
    } else {
      displayValue = safeAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return `${getCurrencySymbol()} ${displayValue}`;
  };

  const getTextSizeClass = (value: string): string => {
    const length = value.length;
    if (length > 20) return 'text-lg';
    if (length > 15) return 'text-xl';
    return 'text-2xl';
  };

  const filteredOrders = useMemo(() => {
    const { start, end } = getDateRange(dateRange, customStartDate, customEndDate);

    return ordersData.filter((order) => {
      const matchesStatus = selectedStatus === 'all' || order.status === selectedStatus;

      if (!matchesStatus) {
        return false;
      }

      if (!start || !end) {
        return true;
      }

      const createdAt = new Date(order.createdAt);
      return createdAt >= start && createdAt <= end;
    });
  }, [ordersData, selectedStatus, dateRange, customStartDate, customEndDate]);

  const totals = useMemo(() => {
    return filteredOrders.reduce(
      (acc, order) => {
        acc.subtotal += Number(order.subtotal || 0);
        acc.tax += Number(order.tax || 0);
        acc.discount += Number(order.discount || 0);
        acc.total += Number(order.total || 0);
        return acc;
      },
      { subtotal: 0, tax: 0, discount: 0, total: 0 }
    );
  }, [filteredOrders]);

  const reportRows = useMemo(() => {
    return filteredOrders.map((order) => ({
      'Order #': order.orderNumber,
      'Date': new Date(order.createdAt).toLocaleString(),
      'Status': order.status,
      'Payment Method': order.paymentMethod || 'N/A',
      'Subtotal': Number(order.subtotal || 0).toFixed(2),
      'Tax': Number(order.tax || 0).toFixed(2),
      'Discount': Number(order.discount || 0).toFixed(2),
      'Total': Number(order.total || 0).toFixed(2),
    }));
  }, [filteredOrders]);

  const topItemsRows = useMemo(() => {
    const aggregate: Record<string, { item: string; quantity: number; revenue: number; orderCount: number }> = {};

    filteredOrders.forEach((order) => {
      const items = (order.items || []) as any[];
      const seenInOrder = new Set<string>();

      items.forEach((item) => {
        const itemName = item.productName || item.product?.name || 'Unknown Item';
        const key = String(itemName);
        const qty = Number(item.quantity || 0);
        const total = Number(item.total || 0);

        if (!aggregate[key]) {
          aggregate[key] = { item: key, quantity: 0, revenue: 0, orderCount: 0 };
        }

        aggregate[key].quantity += qty;
        aggregate[key].revenue += total;
        if (!seenInOrder.has(key)) {
          aggregate[key].orderCount += 1;
          seenInOrder.add(key);
        }
      });
    });

    return Object.values(aggregate)
      .sort((a, b) => b.quantity - a.quantity)
      .map((row) => ({
        'Item': row.item,
        'Quantity Ordered': row.quantity,
        'Orders Count': row.orderCount,
        'Revenue': row.revenue.toFixed(2),
      }));
  }, [filteredOrders]);

  const tableUsageRows = useMemo(() => {
    const aggregate: Record<string, { table: string; orders: number; revenue: number }> = {};

    filteredOrders.forEach((order) => {
      const tableName = getOrderTableName(order);
      if (!tableName) {
        return;
      }

      if (!aggregate[tableName]) {
        aggregate[tableName] = { table: tableName, orders: 0, revenue: 0 };
      }

      aggregate[tableName].orders += 1;
      aggregate[tableName].revenue += Number(order.total || 0);
    });

    return Object.values(aggregate)
      .sort((a, b) => b.orders - a.orders)
      .map((row) => ({
        'Table': row.table,
        'Orders Count': row.orders,
        'Revenue': row.revenue.toFixed(2),
      }));
  }, [filteredOrders]);

  const activeRows = useMemo(() => {
    if (reportType === 'topItems') {
      return topItemsRows;
    }

    if (reportType === 'tableUsage') {
      return tableUsageRows;
    }

    return reportRows;
  }, [reportType, reportRows, topItemsRows, tableUsageRows]);

  const reportLabels = useMemo(() => {
    if (reportType === 'topItems') {
      return {
        title: 'Most Ordered Items Report',
        filePrefix: 'most-ordered-items-report',
        countLabel: 'Items',
      };
    }

    if (reportType === 'tableUsage') {
      return {
        title: 'Most Used Tables Report',
        filePrefix: 'table-usage-report',
        countLabel: 'Tables',
      };
    }

    return {
      title: 'Orders Report',
      filePrefix: 'orders-report',
      countLabel: 'Orders',
    };
  }, [reportType]);

  const handleExportExcel = () => {
    if (activeRows.length === 0) {
      toast.error('No data to export');
      return;
    }

    const summaryRows = reportType === 'orders'
      ? [
          {
            'Order #': 'SUMMARY',
            'Date': '',
            'Status': '',
            'Payment Method': '',
            'Subtotal': totals.subtotal.toFixed(2),
            'Tax': totals.tax.toFixed(2),
            'Discount': totals.discount.toFixed(2),
            'Total': totals.total.toFixed(2),
          },
        ]
      : [];

    const worksheet = XLSX.utils.json_to_sheet([...activeRows, ...summaryRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, reportLabels.title);

    const timestamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${reportLabels.filePrefix}-${timestamp}.xlsx`);
    toast.success('Excel report exported successfully');
  };

  const handleExportPdf = () => {
    if (activeRows.length === 0) {
      toast.error('No data to export');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    const timestamp = new Date().toLocaleString();
    const headers = Object.keys(activeRows[0] as Record<string, unknown>);

    doc.setFontSize(14);
    doc.text(reportLabels.title, 14, 14);
    doc.setFontSize(10);
    doc.text(`Generated: ${timestamp}`, 14, 20);
    doc.text(`${reportLabels.countLabel}: ${activeRows.length}`, 14, 26);

    if (reportType === 'orders') {
      doc.text(`Total Tax: ${formatReportMoney(totals.tax)}`, 14, 32);
    }

    autoTable(doc, {
      startY: reportType === 'orders' ? 38 : 32,
      head: [headers],
      body: activeRows.map((row) => headers.map((header) => String((row as Record<string, unknown>)[header] ?? ''))),
      theme: 'striped',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] },
    });

    if (reportType === 'orders') {
      const finalY = (doc as any).lastAutoTable?.finalY || 40;
      doc.setFontSize(10);
      doc.text(`Summary - Subtotal: ${formatReportMoney(totals.subtotal)}`, 14, finalY + 10);
      doc.text(`Summary - Tax: ${formatReportMoney(totals.tax)}`, 14, finalY + 16);
      doc.text(`Summary - Discount: ${formatReportMoney(totals.discount)}`, 14, finalY + 22);
      doc.text(`Summary - Total: ${formatReportMoney(totals.total)}`, 14, finalY + 28);
    }

    const datePart = new Date().toISOString().slice(0, 10);
    doc.save(`${reportLabels.filePrefix}-${datePart}.pdf`);
    toast.success('PDF report exported successfully');
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="reports"
        onNavigate={onNavigate}
        ordersCount={stats?.total || ordersData.length}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'}`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reports</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Generate and export operational reports: orders, top items, and table usage.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={refresh}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Export Excel
                </button>
                <button
                  onClick={handleExportPdf}
                  className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                >
                  <FileText className="h-4 w-4" />
                  Export PDF
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setReportType('orders')}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${reportType === 'orders' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Orders
                </button>
                <button
                  onClick={() => setReportType('topItems')}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${reportType === 'topItems' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                >
                  <UtensilsCrossed className="h-4 w-4" />
                  Most Ordered Items
                </button>
                <button
                  onClick={() => setReportType('tableUsage')}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${reportType === 'tableUsage' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                >
                  <Table2 className="h-4 w-4" />
                  Most Used Tables
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs text-gray-500 dark:text-gray-400">{reportLabels.countLabel}</p>
                <p className={`mt-1 font-bold text-gray-900 dark:text-white ${getTextSizeClass(String(activeRows.length))}`}>{activeRows.length}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs text-gray-500 dark:text-gray-400">Subtotal</p>
                <p className={`mt-1 font-bold text-gray-900 dark:text-white ${getTextSizeClass(formatReportMoneyCompact(totals.subtotal))}`}>{formatReportMoneyCompact(totals.subtotal)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Tax</p>
                <p className={`mt-1 font-bold text-orange-600 dark:text-orange-400 ${getTextSizeClass(formatReportMoneyCompact(totals.tax))}`}>{formatReportMoneyCompact(totals.tax)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Discounts</p>
                <p className={`mt-1 font-bold text-rose-600 dark:text-rose-400 ${getTextSizeClass(formatReportMoneyCompact(totals.discount))}`}>{formatReportMoneyCompact(totals.discount)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
                <p className="text-xs text-gray-500 dark:text-gray-400">Grand Total</p>
                <p className={`mt-1 font-bold text-indigo-600 dark:text-indigo-400 ${getTextSizeClass(formatReportMoneyCompact(totals.total))}`}>{formatReportMoneyCompact(totals.total)}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">Date Range</label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value as DateRangeType)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">Custom Start</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    disabled={dateRange !== 'custom'}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">Custom End</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    disabled={dateRange !== 'custom'}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-800">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{reportLabels.title}</h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">{activeRows.length} rows</span>
              </div>

              {loading ? (
                <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading orders...</div>
              ) : error ? (
                <div className="p-6 text-sm text-red-600 dark:text-red-400">{error}</div>
              ) : activeRows.length === 0 ? (
                <div className="p-6 text-sm text-gray-500 dark:text-gray-400">No orders found for selected filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800/60">
                        {Object.keys(activeRows[0]).map((header) => (
                          <th
                            key={header}
                            className={`px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 ${header.toLowerCase().includes('total') || header.toLowerCase().includes('revenue') || header.toLowerCase().includes('tax') || header.toLowerCase().includes('quantity') || header.toLowerCase().includes('count') || header.toLowerCase().includes('subtotal') || header.toLowerCase().includes('discount') ? 'text-right' : 'text-left'}`}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeRows.map((row, index) => (
                        <tr key={`${reportType}-${index}`} className="border-t border-gray-100 dark:border-gray-800">
                          {Object.entries(row).map(([key, value]) => {
                            const isNumeric = key.toLowerCase().includes('total') || key.toLowerCase().includes('revenue') || key.toLowerCase().includes('tax') || key.toLowerCase().includes('quantity') || key.toLowerCase().includes('count') || key.toLowerCase().includes('subtotal') || key.toLowerCase().includes('discount');
                            const isMoney = key.toLowerCase().includes('total') || key.toLowerCase().includes('revenue') || key.toLowerCase().includes('tax') || key.toLowerCase().includes('subtotal') || key.toLowerCase().includes('discount');
                            const numericValue = Number(value);

                            return (
                              <td
                                key={key}
                                className={`px-4 py-3 ${isNumeric ? 'text-right' : 'text-left'} ${isMoney ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                              >
                                {isMoney && !isNaN(numericValue) ? formatReportMoney(numericValue) : String(value)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleExportExcel}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Download className="h-4 w-4" />
                Quick Export
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
